<template>
  <main class="cashbox-manual">
    <modal-dialog
      content-class="cashbox-manual-modal"
      v-bind:title="modalDialogTransactionTitle"
      v-model="isShowCashboxTransactionDialog"
      width="100%"
      max-width="550px"
      with-cross
    >
      <form
        class="cashbox-manual-modal__form"
        v-on:submit.prevent="onSubmitTransaction"
      >
        <input-radio-with-bullets
          class="cashbox-manual__withdrawal-modes"
          identifier="mode"
          direction="row"
          v-model="selectedWithdrawalMode"
          v-bind:items="withdrawalModeOptions"
        />
        <input-number
          class="cashbox-manual-modal__input"
          v-bind:title="'Сумма'"
          v-bind:min="'0'"
          currency
          v-bind:name="'amount'"
          v-bind:required="true"
          v-model="amount"
          v-bind:withoutNativeControls="true"
        />
        <template v-if="isOut">
          <autocomplete-input
            class="cashbox-manual-modal__input-select"
            clearable
            outlined
            dense
            title="Сотрудник"
            placeholder="Выберите сотрудника"
            item-text="title"
            item-value="value"
            v-bind:items="employees"
            v-model="selectedWithdrawalEmployee"
            v-bind:menu-props="{
              contentClass: 'text-big',
              zIndex: '1',
            }"
          />
          <autocomplete-input
            class="cashbox-manual-modal__input-select"
            can-delete-items
            item-text="name"
            item-value="id"
            title="Цель"
            placeholder="Выберите цель"
            outlined
            dense
            v-bind:items="withdrawalPurposes"
            v-model="selectedWithdrawalPurpose"
            v-on:delete-item="onRemoveWithdrawalPurpose"
            v-bind:menu-props="{
              contentClass: 'text-big',
              zIndex: '1',
            }"
          />
          <input-text
            v-if="needsCreatingWithdrawalPurpose"
            required
            title="Цель"
            v-bind:violations="withdrawalPurposeNameInputErrors"
            v-letter-capital
            v-model="customWithdrawalPurposeName"
          />
        </template>
        <textarea-length-limitation
          class="cashbox-manual__description"
          v-bind:title="`Примечание`"
          v-bind:max-length="200"
          v-bind:violations="noteInputErrors"
          v-model="note"
          v-letter-capital
          rows="3"
        />
        <div class="cashbox-manual-modal__actions">
          <button
            class="cashbox-manual-modal__cancel"
            type="button"
            v-on:click="isShowCashboxTransactionDialog = false"
          >Отмена</button>
          <button
            class="cashbox-manual-modal__submit"
            type="submit"
            v-bind:disabled="isAwaitRequest"
          >Подтвердить</button>
        </div>
      </form>
    </modal-dialog>
    <modal-dialog
      content-class="cashbox-manual-modal"
      v-bind:title="cashboxOperationTitle"
      v-bind:value="selectedCashboxOperation.isSome()"
      v-on:input="onCloseChangeOperationModal"
      width="100%"
      max-width="550px"
      with-cross
    >
      <form
        v-if="selectedCashboxOperation.isSome()"
        class="cashbox-manual-modal__form"
        v-on:submit.prevent="onSubmitOperation"
      >
        <input-radio-with-bullets
          class="cashbox-manual__withdrawal-modes"
          identifier="out-mode"
          direction="row"
          v-model="cashboxOperationProps.payment_mode"
          v-bind:items="withdrawalModeOptions"
        />
        <input-number
          class="cashbox-manual-modal__input"
          title="Сумма"
          name="amount"
          currency
          v-bind:min="'0'"
          v-bind:required="true"
          v-model="cashboxOperationProps.amount"
          v-bind:withoutNativeControls="true"
        />
        <div class="cashbox-manual-modal__actions">
          <button
            class="cashbox-manual-modal__cancel"
            type="button"
            v-on:click="onCloseChangeOperationModal"
          >Отмена</button>
          <button
            class="cashbox-manual-modal__submit"
            type="submit"
            v-bind:disabled="isAwaitChangedRequest"
          >Подтвердить</button>
        </div>
      </form>
    </modal-dialog>
    <div class="cashbox-manual__header">
      <h1>Касса</h1>
      <input-select
        option-title-key="name"
        option-value-key="id"
        title=""
        input-class="cashbox-manual__header__input-cashbox"
        v-bind:options="cashboxList"
        v-bind:value="selectedCashboxId"
        v-on:input="onInputCashbox"
      />
      <div
        v-if="cashboxDataLoaded"
        class="cashbox-manual__header__balance header-balance">
        <p>
          <span class="header-balance__text">
            Текущий баланс (наличные средства):
          </span>
          <span>{{ cashboxCashBalance }}</span>
        </p>
        <p>
          <span class="header-balance__text">
            Текущий баланс (безналичные средства):
          </span>
          <span>{{ cashboxCashlessBalance }}</span>
        </p>
      </div>
      <date-range-with-presets
        v-bind:presets="periodPresets"
        v-if="periodDates.length"
        v-model="periodDates"
        v-on:input="onChangePeriod" />
      <div class="cashbox-manual__header__buttons">
        <button
          class="cashbox-manual__header__button button-entry"
          type="button"
          v-on:click="depositFunds"
        >Внесение</button>
        <button
          class="cashbox-manual__header__button button-removal"
          type="button"
          v-on:click="withdrawFunds"
        >Изъятие</button>
        <button
          type="button"
          v-on:click="fetchReportSpreadsheet"
          class="cashbox-manual__header__button-excel"
        >EXCEL</button>
      </div>
    </div>
    <em v-if="isFetchingForOperations">Выполняется получение операций...</em>
    <strong v-else-if="hasNoOperations">Отсутствуют операции по счетам</strong>
    <div v-else class="cashbox-manual__table-wrapper">
      <table
        class="cashbox-manual__table cashbox-manual-table"
      >
        <thead class="cashbox-manual-table__head">
          <tr>
            <th
              class="cashbox-manual-table__head-cell"
              v-bind:class="{ employee: title === 'Сотрудник' }"
              v-bind:key="`cashbox-columns-${columnIndex}`"
              v-for="(title, columnIndex) in columns"
            >{{ title }}</th>
            <th class="cashbox-manual-table__head-cell pen" />
          </tr>
        </thead>
        <tbody>
          <tr
            class="cashbox-manual-table__body-line"
            v-bind:key="lineKey"
            v-bind:class="[classes, cells[index]?.type]"
            v-for="({
              cells, classes, expanded, lineKey, canChange, operation,
            }, index) in bodyLines"
          >
            <td
              class="cashbox-manual-table__body-cell"
              v-bind:key="cellKey"
              v-for="({
                cellKey,
                hasShowMore,
                title,
                to,
                note,
                employee,
              }, index) in cells"
            >
              <template v-if="hasShowMore">
                <label
                  class="show-more-cell"
                  title="Показать за день"
                  v-bind:class="{ expanded }"
                >
                  <span class="show-more-cell__icon" />
                  <span>{{ title }}</span>
                  <input
                    class="show-more-cell__mark"
                    type="checkbox"
                    v-bind:value="lineKey"
                    v-model="expandedGroups">
                </label>
              </template>
              <router-link
                class="cashbox-manual-table__link target"
                v-bind:to="to"
                v-else-if="to"
              >{{ title }}</router-link>
              <autocomplete-input
                v-else-if="employee"
                class="cashbox-manual-table__input"
                clearable
                item-text="title"
                item-value="value"
                label="Сотрудник"
                v-bind:items="employees"
                v-bind:attach="false"
                v-bind:value="title"
                v-on:change="onInputEmployee(employee.id, $event, cellKey)"
                v-bind:menu-props="{
                  contentClass: 'text-small',
                }"
              />
              <v-textarea
                v-else-if="note"
                class="cashbox-manual-table__textarea"
                v-bind:value="title"
                v-on:change="onInputNote(note.id, $event, cellKey)"
                no-resize
                rows="1"
                minlength="1"
                maxlength="200"
                auto-grow
                hide-details
              />
              <span
                v-else
                v-bind:class="{ target: index === 3 || index === 6 }">
                {{ title }}
              </span>
            </td>
            <td v-if="canChange" class="cashbox-manual-table__body-cell">
              <button
                class="cashbox-manual-table__icons-edit"
                type="button"
                v-on:click="onOpenChangeOperationModal(operation)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </main>
</template>

<script>
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { convertToInputDate, formatDateString } from '@/utils/timeFormat';
import ModalDialog from '@/components/ModalDialog.vue';
import InputNumber from '@/components/InputNumber.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import InputSelect from '@/components/InputSelect.vue';
import AutocompleteInput from '@/components/AutocompleteInput.vue';
import InputText from '@/components/InputText.vue';
import { CashInOutRequest } from '@/storage/gateways/CashboxGateway';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import InputRadioWithBullets from '@/components/InputRadioWithBullets.vue';
import { STORE_CASHBOX_FILTERS } from '@/store/actions/cashboxFilters';
import { Maybe } from 'monet';

/**
 * @description Идентификатор уникальной цели изъятия из кассы.
 * @type {string}
 */
const ID_CUSTOM_WITHDRAWAL_PURPOSE = 'new';

/**
 * @description Способ изъятия наличные.
 * @type {string}
 */
const WITHDRAWAL_MODE_CASH = 'cash';

const makeGroupKey = (itemIndex) => (lineIndex) => `cashbox-lines:${itemIndex}:${lineIndex}`;

const mapLinkToLine = (linkTo) => {
  const [section, id, invoiceId] = linkTo.split(',', 3);
  if (section === 'invoices') {
    const to = (invoiceId !== undefined)
      ? {
        name: 'ClinicPanelPatientInvoice',
        params: {
          id,
          invoiceId,
        },
      }
      : {
        name: 'ClinicPanelPatientInvoiceList',
        params: {
          id,
        },
      };
    return (index) => ((index === 3) ? to : null);
  }
  return () => null;
};

const mapNoteToLine = (linkTo) => {
  const [section, id] = linkTo.split(',', 2);
  if (section === 'cashInOut') {
    return (index) => ((index === 9) ? { id } : null);
  }
  return () => null;
};

const mapEmployeeToLine = (line) => {
  const { link_to: linkTo, type, restrict_edit: restrictEdit } = line;
  const [section, id] = linkTo.split(',', 2);
  const typeIsOut = type === 'out' || type === 'cashless-out';
  if (section === 'cashInOut' && typeIsOut && !restrictEdit) {
    return (index) => ((index === 8) ? { id } : null);
  }
  return () => null;
};

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

/**
 * @description Примесь изменения операции.
 */
const changeOperationMixin = {
  computed: {
    cashboxOperationTitle() {
      return this.cashboxOperationProps.type === 'in'
        ? 'Изменение операции внесения'
        : 'Изменение операции изъятия';
    },
    cashboxOperationProps() {
      return this.selectedCashboxOperation.fold({})((operation) => operation);
    },
  },
  data() {
    return {
      isShowChangeOperationDialog: false,
      selectedCashboxOperation: Maybe.none(),
      isAwaitChangedRequest: false,
    };
  },
  methods: {
    onOpenChangeOperationModal(operation) {
      this.selectedCashboxOperation = Maybe.of({ ...operation });
    },
    onCloseChangeOperationModal() {
      this.selectedCashboxOperation = Maybe.none();
    },
    onSubmitOperation() {
      this.isAwaitChangedRequest = true;
      const onSuccess = (response) => {
        this.notifySuccess(response.message);
        this.fetchOperations(this.selectedCashboxId, true);
        this.onCloseChangeOperationModal();
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      this
        .$cashboxGateway
        .changeOperation(
          this.cashboxOperationProps.id,
          this.cashboxOperationProps.payment_mode,
          this.cashboxOperationProps.amount,
        )
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitChangedRequest = false; });
    },
  },
};

/**
 * @description Раздел кассы без провайдера.
 */
export default {
  components: {
    TextareaLengthLimitation,
    ModalDialog,
    DateRangeWithPresets,
    InputNumber,
    InputSelect,
    AutocompleteInput,
    InputText,
    InputRadioWithBullets,
  },
  computed: {
    /**
     * @description Заголовок модального окна операций.
     * @return {string}
     */
    modalDialogTransactionTitle() {
      return this.isOut ? 'Изъятие суммы' : 'Внесение суммы';
    },
    /**
     * @description Строки таблицы.
     */
    bodyLines() {
      return this.items.flatMap((item, itemIndex) => {
        const groupKeyPrefix = makeGroupKey(itemIndex);
        const groupKey = groupKeyPrefix('head');
        const groupCells = item.group_line.map(
          (title, groupCellIndex) => {
            const cellKey = `${groupKey}:${groupCellIndex}`;
            return {
              cellKey,
              hasShowMore: groupCellIndex === 0,
              title,
              to: null,
              classes: 'group',
            };
          },
        );
        const expanded = this.expandedGroups.includes(groupKey);
        const lines = (expanded ? item.lines : []).flatMap((line, lineIndex) => {
          const lineKey = groupKeyPrefix(lineIndex);
          const lineLinkTo = mapLinkToLine(line.link_to);
          const noteToLine = mapNoteToLine(line.link_to);
          const employeeToLine = mapEmployeeToLine(line);
          const lineCells = line.columns.map((title, cellIndex) => {
            const cellKey = `${lineKey}:${cellIndex}`;
            return {
              cellKey,
              hasShowMore: false,
              title,
              type: line?.type || '',
              to: lineLinkTo(cellIndex),
              note: noteToLine(cellIndex),
              employee: employeeToLine(cellIndex),
            };
          });
          return {
            operation: line.operation,
            lineKey,
            cells: lineCells,
            classes: '',
            canChange: line.actions.includes('change'),
          };
        });
        return [
          {
            expanded, lineKey: groupKey, cells: groupCells, classes: 'group',
          },
          ...lines,
        ];
      });
    },
    /**
     * @description Не имеет операций.
     */
    hasNoOperations() {
      return this.items.length === 0;
    },
    /**
     * @description Предварительные установки периодов.
     * @returns {({toValue: function(): [Date,Date], id: string, label: string, title: string}[])}
     */
    periodPresets() {
      return this.periodTypes.map(({ fromTo: [from, to], title, value }) => ({
        id: value,
        label: title,
        title,
        toValue: () => [new Date(from), new Date(to)],
      }));
    },
    noteInputErrors() {
      return 'note' in this.cashInOutInputErrors ? this.cashInOutInputErrors.note : [];
    },
    withdrawalPurposeNameInputErrors() {
      return 'withdrawal-purpose-name' in this.cashInOutInputErrors
        ? this.cashInOutInputErrors['withdrawal-purpose-name']
        : [];
    },
    withdrawalPurposes() {
      return [...this.withdrawalPurposeList, { name: 'Добавить цель', id: ID_CUSTOM_WITHDRAWAL_PURPOSE }];
    },
    needsCreatingWithdrawalPurpose() {
      return this.selectedWithdrawalPurpose
        && this.selectedWithdrawalPurpose === ID_CUSTOM_WITHDRAWAL_PURPOSE;
    },
  },
  created() {
    this.fetchOptions().then(() => {
      this.fetchWithdrawalPurposeList();
      if (
        this.$store.getters.isNeedConfirmCashboxFilters
        && this.$store.getters.getCashboxFilters
      ) {
        this.applyFilters(this.$store.getters.getCashboxFilters);
      } else {
        this
          .periodPresets
          .slice(0, 1)
          .map(({ toValue }) => toValue())
          .forEach((value) => { this.periodDates = value; });
      }
      this.fetchOperations(this.selectedCashboxId);
    });
  },
  data() {
    return {
      /**
       * @description Столбцы операций.
       * @type {string[]}
       */
      columns: [],
      /**
       * @description Сотрудники.
       * @type {array}
       */
      employees: [],
      /**
       * @description Раскрытые группы.
       * @type {string[]}
       */
      expandedGroups: [],
      /**
       * @description Ожидается получение операций.
       * @type {boolean}
       */
      isFetchingForOperations: true,
      /**
       * @description Группы операций.
       * @type {({lines: {columns: string[][]}[], title: string}[])}
       */
      items: [],
      /**
       * @description Выбранные период дней.
       * @type {([Date, Date])}
       */
      periodDates: [],
      /**
       * @description Типы периодов.
       * @type {({fromTo: string[], title: string, value: string}[])}
       */
      periodTypes: [],
      /**
       * @description Показать окно операций пополнения/изъятия средств кассы.
       * @type {boolean}
       */
      isShowCashboxTransactionDialog: false,
      /**
       * @description Сумма.
       * @type {number}
       */
      amount: 0,
      /**
       * @description Операция снятия.
       * @type {boolean}
       */
      isOut: false,
      /**
       * @description Примечание операции.
       * @type {string}
       */
      note: '',
      cashInOutInputErrors: {},
      cashboxDataLoaded: false,
      cashboxCashBalance: '',
      cashboxCashlessBalance: '',
      isAwaitRequest: false,
      selectedWithdrawalPurpose: null,
      selectedWithdrawalEmployee: null,
      withdrawalPurposeList: [],
      customWithdrawalPurposeName: '',
      selectedWithdrawalMode: WITHDRAWAL_MODE_CASH,
      withdrawalModeOptions: [
        { title: 'Наличные', value: WITHDRAWAL_MODE_CASH },
        { title: 'Безналичные', value: 'cashless' },
      ],
    };
  },
  methods: {
    /**
     * @description Применить сохраненные фильтры.
     * @param {Object} filters
     */
    applyFilters(filters) {
      if (filters && filters.periodDates && filters.periodDates.length === 2) {
        this.periodDates = filters
          .periodDates
          .map((dateStr) => (dateStr ? new Date(dateStr) : null));
      }
    },
    /**
     * @description Сохранить фильтры в хранилище.
     * @returns {void}
     */
    storeFilters() {
      const filters = {
        periodDates: this.periodDates
          .map((date) => (date instanceof Date ? date.toISOString() : date)),
      };
      this.$store.dispatch(STORE_CASHBOX_FILTERS, filters);
    },
    fetchWithdrawalPurposeList() {
      const onSuccess = (response) => { this.withdrawalPurposeList = response; };
      this.$cashboxGateway.viewWithdrawalPurposes().then((result) => result.forEach(onSuccess));
    },
    async onRemoveWithdrawalPurpose(purposeId) {
      const dialog = await this.showAcceptDialog('Вы действительно хотите удалить цель?');
      if (dialog.result !== DialogResult.YES) {
        return;
      }
      const onSuccess = (message) => {
        if (this.selectedWithdrawalPurpose === purposeId) {
          this.selectedWithdrawalPurpose = null;
        }
        this.notifySuccess(message);
        this.fetchWithdrawalPurposeList();
      };
      this
        .$cashboxGateway
        .removeWithdrawalPurpose(purposeId)
        .then((result) => result.fold(this.notifyFail, onSuccess));
    },
    /**
     * @description Внести средства.
     * @returns {void}
     */
    depositFunds() {
      this.isOut = false;
      this.isShowCashboxTransactionDialog = true;
    },
    fetchCashboxBalance(cashboxId) {
      this
        .$cashboxGateway
        .viewCashboxBalance(cashboxId)
        .then(
          (result) => {
            result.forEachLeft(({ title }) => { this.notifyFail(title); });
            result.forEach(
              ({
                cashBalance,
                cashlessBalance,
              }) => {
                this.cashboxCashBalance = cashBalance;
                this.cashboxCashlessBalance = cashlessBalance;
              },
            );
          },
        )
        .finally(() => { this.cashboxDataLoaded = true; });
    },
    /**
     * @description Получить операции.
     * @param {number} cashboxId Идентификатор кассы.
     * @returns {void}
     */
    fetchOperations(cashboxId, preExpandedGroups = false) {
      const dates = this.periodDates.map(convertToInputDate);
      this.isFetchingForOperations = true;
      this
        .$cashboxGateway
        .viewOperationsAllPatientInvoicesForPeriod(cashboxId, ...dates)
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ columns, items }) => {
            this.columns = columns;
            this.items = items;
            if (preExpandedGroups !== true) {
              this.expandedGroups = (this.items.length === 1)
                ? [makeGroupKey(0)('head')]
                : [];
            }
            this.fetchCashboxBalance(this.selectedCashboxId);
          });
        })
        .finally(() => { this.isFetchingForOperations = false; });
    },
    /**
     * @description Получить опции.
     * @returns {Promise<void>}
     */
    fetchOptions() {
      return this
        .$cashboxGateway
        .viewInvoiceOperationOptions()
        .then((result) => {
          result.forEach(({ periodTypes, employees }) => {
            this.periodTypes = periodTypes;
            this.employees = employees;
          });
        });
    },
    /**
     * @description Получить операции всех счетов пациентов за период в виде таблицы.
     * @returns {void}
     */
    fetchReportSpreadsheet() {
      const [dateFrom, dateTo] = this.periodDates.map(formatDateString);
      const onSuccess = (response) => {
        saveFile(response, `Отчет по кассе ${dateFrom} - ${dateTo}.xls`);
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const dates = this.periodDates.map(convertToInputDate);
      const cashboxId = this.selectedCashboxId;
      this
        .$cashboxGateway
        .viewOperationsAllPatientInvoicesForPeriodBySheet(cashboxId, ...dates)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать смену периода.
     * @returns {void}
     */
    onChangePeriod() {
      this.storeFilters();
      this.fetchOperations(this.selectedCashboxId);
    },
    /**
     * @description Изъять средства.
     * @returns {void}
     */
    withdrawFunds() {
      this.isOut = true;
      this.isShowCashboxTransactionDialog = true;
    },
    /**
     * @description Провести операцию по средствам.
     * @returns {void}
     */
    onSubmitTransaction() {
      this.cashInOutInputErrors = {};
      this.isAwaitRequest = true;
      let command = 'in';
      if (this.isOut) {
        command = this.selectedWithdrawalMode === WITHDRAWAL_MODE_CASH
          ? 'out'
          : 'cashless-out';
      } else {
        command = this.selectedWithdrawalMode === WITHDRAWAL_MODE_CASH
          ? 'in'
          : 'cashless-in';
      }
      const amountToKop = this.amount * 100;
      const request = new CashInOutRequest(
        this.selectedCashboxId,
        command,
        amountToKop,
        this.note,
        this.selectedWithdrawalPurpose,
        this.customWithdrawalPurposeName,
        this.selectedWithdrawalEmployee,
      );
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.cashInOutInputErrors = error.allCauses();
      };
      const onSuccess = (result) => {
        this.notifySuccess(result.title);
        this.isShowCashboxTransactionDialog = false;
        this.amount = 0;
        this.note = '';
        this.selectedWithdrawalMode = WITHDRAWAL_MODE_CASH;
        if (this.selectedWithdrawalPurpose === ID_CUSTOM_WITHDRAWAL_PURPOSE) {
          this.fetchWithdrawalPurposeList();
        }
        this.customWithdrawalPurposeName = '';
        this.selectedWithdrawalPurpose = null;
        this.selectedWithdrawalEmployee = null;
        this.fetchOperations(this.selectedCashboxId);
        this.fetchCashboxBalance(this.selectedCashboxId);
      };
      this
        .$cashboxGateway
        .cashInOut(request)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
    onInputCashbox(cashboxId) {
      this.storeFilters();
      this.$emit('input-cashbox', cashboxId);
    },
    onInputNote(operationId, value, cellKey) {
      const [, dayIndex, rowIndex, cellIndex] = cellKey.split(':');
      this.items[dayIndex].lines[rowIndex].columns[cellIndex] = value;
      this
        .$cashboxGateway
        .editCashboxOperationNote(operationId, value)
        .then((result) => result.fold(this.notifyFail, this.notifySuccess));
    },
    /**
     * @description Обработать выбор сотрудника относящегося к операции.
     * @param {string} operationId
     * @param {string} value
     * @param {string} cellKey
     * @returns {void}
     */
    onInputEmployee(operationId, value, cellKey) {
      const [, dayIndex, rowIndex, cellIndex] = cellKey.split(':');
      this.items[dayIndex].lines[rowIndex].columns[cellIndex] = value;
      this
        .$cashboxGateway
        .editCashboxOperationEmployee(operationId, value)
        .then((result) => result.fold(this.notifyFail, this.notifySuccess));
    },
  },
  mixins: [changeOperationMixin, builtInDialogMixin, notifierMixin],
  name: 'ClinicPanelCashboxManual',
  props: {
    cashboxList: { required: true, type: Array },
    selectedCashboxId: { required: true, type: Number },
  },
  watch: {
    periodDates: {
      handler() {
        this.storeFilters();
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.cashbox-manual-modal {
  &__form {
    display: grid;
    row-gap: 26px;
  }
  &__input {
    max-width: 200px;
  }
  &__input-select {
    .v-input {
      columns: red !important;
      &:not(.v-input--is-focused):not(.v-input--has-state) fieldset {
        border: 1px solid var(--border-color);
      }
      &.primary--text {
        color: var(--action-color) !important;
      }
    }
  }
  &__actions {
    display: flex;
    gap: 10px;
  }
  &__cancel {
    @include buttons.btn-transparent(100%, 48px, 16px);
  }
  &__submit {
    @include buttons.btn-blue(100%, 48px, 16px);
  }
}

.cashbox-manual {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: auto;
  padding: 1.25rem 1rem;
  &__header {
    display: grid;
    gap: 20px;
    width: 100%;
    padding: 20px 20px 20px 20px;
    &__balance {
      display: grid;
      gap: 10px;
      p {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        margin: 0;
      }
    }
    .header-balance {
      &__text {
        display: flex;
        align-items: center;
        column-gap: 5px;
        width: min(100%, 330px);
      }
    }
    &__buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
    &__button {
      @include buttons.btn-blue(210px, 48px, 16px);
      gap: 6px;
      &:is(.button-entry, .button-removal) {
        &::before {
          content: '';
          flex-shrink: 0;
          width: 18px;
          height: 19px;
        }
      }
      &.button-entry {
        &::before {
          mask: url("@/assets/img/plus.svg") no-repeat 50%;
          mask-size: 18px 19px;
          background-color: #ffffff;
        }
      }
      &.button-removal {
        &::before {
          background: url("@/assets/img/minus-rounded.svg") no-repeat 50%;
          background-size: 18px 19px;
        }
      }
    }
    &__button-excel {
      @include buttons.btn-transparent(210px, 48px, 16px);
      &:before {
        content: "";
        background-image: url("@/assets/img/exel.svg");
        background-repeat: no-repeat;
        background-size: contain;
        width: 16px;
        height: 16px;
        margin: 0 8px 0 0;
      }
    }
    &__input-cashbox {
      margin-top: 0;
      padding-top: 0;
      max-width: 505px;
      margin-bottom: 15px;
    }
  }
  &__description {
    margin-top: 5px;
    .textarea-length-limitation__textarea {
      min-height: 80px;
      &:focus-visible {
        background-color: var(--primary-background-color);
        border: 2px solid var(--action-color);
        color: var(--reading-color);
        outline: 0;
      }
    }
  }
  &__table-wrapper {
    overflow-x: auto;
  }
}

.cashbox-manual,
.cashbox-manual .cashbox-manual__table {
  width: 100%;
}

.cashbox-manual-table {
  min-width: 1680px;
  border-collapse: collapse;
  font-size: .875rem;
  line-height: 1rem;
  overflow: auto;
  table-layout: fixed;
  &__input {
    .v-input, .v-label {
      font-size: 14px;
    }
  }
  &__textarea.v-input{
    font-size: 14px;
  }
}

.cashbox-manual-table .cashbox-manual-table__body-cell {
  padding: 15px 8px;
  text-align: left;
  text-transform: none;
  white-space: pre-line;
  overflow-wrap: anywhere;

  &.pen {
    background-image: url("@/assets/img/pen-grey.svg");
    background-size: contain;
    background-repeat: no-repeat;
    width: 15px;
    height: 15px;
    padding: 0;
    margin-left: 14px;
    cursor: pointer;
    &:hover {
      background-image: url("@/assets/img/pen-blue.svg");
    }
  }
}

.cashbox-manual-table .cashbox-manual-table__icons {
  &-edit {
    background-image: url("@/assets/img/pen-grey.svg");
    background-size: contain;
    background-repeat: no-repeat;
    width: 15px;
    height: 15px;
    padding: 0;
    margin-left: 6px;
    cursor: pointer;
    &:hover {
      background-image: url("@/assets/img/pen-blue.svg");
    }
  }
}

.cashbox-manual-table .cashbox-manual-table__body-line {
  border-bottom: 1px solid var(--fill-color);
  &:is(.out, .refund, .cashless-out) {
    .target {
      color: #e00404b0;
    }
  }
}

.cashbox-manual-table .cashbox-manual-table__body-line.group {
  font-weight: bold;
}

.cashbox-manual-table .cashbox-manual-table__head {
  border-bottom: 2px solid var(--action-color);
  font-weight: 700;
}

.cashbox-manual-table .cashbox-manual-table__head-cell {
  position: sticky;
  top: 0;
  padding: 10px 8px;
  text-align: left;
  background-color: #ffffff;
  z-index: 1;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 100%;
    border-bottom: 2px solid var(--action-color);
  }
  &:first-of-type {
    width: 100px;
  }
  &:nth-of-type(2),
  &:nth-of-type(n + 5):nth-of-type(-n+7) {
    width: 150px;
  }
  &:nth-of-type(8) {
    width: 180px;
  }
  &:last-of-type {
    width: 300px;
  }
  &.employee {
    width: 250px;
  }
  &.pen {
    width: 40px;
  }
}

.cashbox-manual-table .cashbox-manual-table__link:hover {
  text-decoration: none;
}

.show-more-cell {
  position: relative;
}

.show-more-cell .show-more-cell__mark {
  opacity: 0;
  position: absolute;
  z-index: -1;
}

.show-more-cell .show-more-cell__icon {
  align-items: center;
  background-image: url("@/assets/img/checked/arrow-top-blue.svg");
  background-position: bottom;
  background-size: contain;
  cursor: pointer;
  display: inline-flex;
  height: 15px;
  margin: 0 17px 0 0;
  user-select: none;
  width: 15px;
}

.show-more-cell.expanded .show-more-cell__icon {
  background-position: top;
  transform: rotate(180deg);
}

@media screen and (max-width: 380px) {
  .cashbox-manual-modal {
    &__input {
      max-width: 100%;
    }
  }
}
</style>
